/**
 * 抽壳特征会话桥接层。
 *
 * 设计说明：
 * 1. 只处理编辑会话输入输出投影，不承担几何求解；
 * 2. 抽壳编辑定义就是 ShellFeatureDefinition 本身，没有 blend 的二级领域结构。
 */

import { createFeatureReferenceBinding } from '../../../utils/feature/createFeatureReferenceBinding';
import type { FeatureReferenceBinding } from '../../binding/FeatureReferenceBinding';
import { buildShellModelTopologyBindings } from '../../binding/buildShellModelTopologyBindings';
import { ShellFeatureDefinitionModel } from '../../definition/shell/ShellFeatureDefinition';
import type { ShellFeaturePrototype } from '../../prototype/shell/ShellFeaturePrototype';
import type { FeatureEditSession } from '../FeatureEditSession';

/**
 * 抽壳编辑定义。
 */
export type ShellFeatureEditDefinition = ShellFeatureDefinition;

/**
 * 抽壳编辑会话载荷。
 */
export interface ShellEditSessionPayload {
    /** 当前编辑会话。 */
    session: FeatureEditSession | null;
    /** 当前抽壳定义。 */
    definition: ShellFeatureEditDefinition | null;
    /** 上游实体对象 ID。 */
    upstreamBodyObjectId: string | null;
    /** 特征名称。 */
    name: string | null;
}

/**
 * 抽壳特征会话桥接层。
 */
export class ShellFeatureSessionBridge {
    /**
     * 从 prototype 恢复编辑定义。
     *
     * @param prototype 抽壳原型。
     * @returns 编辑定义。
     */
    public restoreDefinitionFromPrototype(prototype: ShellFeaturePrototype): ShellFeatureEditDefinition {
        return structuredClone(prototype.getDefinition().getShellDefinition());
    }

    /**
     * 构建预览输入。
     *
     * @param input 输入参数。
     * @returns 预览执行载荷。
     */
    public buildPreviewInput(input: {
        featureId?: string | null;
        prototype: ShellFeaturePrototype;
        definition: ShellFeatureEditDefinition;
    }): {
        featureKind: 'shell';
        featureId?: string | null;
        definition: ShellFeatureDefinitionModel;
        bindings: FeatureReferenceBinding[];
    } {
        return this.buildExecutionInput(input);
    }

    /**
     * 构建提交输入。
     *
     * @param input 输入参数。
     * @returns 提交执行载荷。
     */
    public buildCommitInput(input: {
        featureId?: string | null;
        prototype: ShellFeaturePrototype;
        definition: ShellFeatureEditDefinition;
    }): {
        featureKind: 'shell';
        featureId?: string | null;
        definition: ShellFeatureDefinitionModel;
        bindings: FeatureReferenceBinding[];
    } {
        return this.buildExecutionInput(input);
    }

    /**
     * 构建宿主侧编辑会话载荷。
     *
     * @param session 编辑会话。
     * @returns 宿主可消费的载荷。
     */
    public buildEditSessionPayload(session: FeatureEditSession | null): ShellEditSessionPayload {
        if (!session) {
            return {
                session: null,
                definition: null,
                upstreamBodyObjectId: null,
                name: null,
            };
        }

        const prototype = session.getPrototype() as ShellFeaturePrototype;
        const projection = prototype.getDefinition().toProjection();
        return {
            session,
            definition: this.restoreDefinitionFromPrototype(prototype),
            upstreamBodyObjectId: projection.upstreamBodyObjectId,
            name: projection.name,
        };
    }

    /**
     * 统一构建执行输入。
     *
     * @param input 输入参数。
     * @returns 执行载荷。
     */
    private buildExecutionInput(input: {
        featureId?: string | null;
        prototype: ShellFeaturePrototype;
        definition: ShellFeatureEditDefinition;
    }): {
        featureKind: 'shell';
        featureId?: string | null;
        definition: ShellFeatureDefinitionModel;
        bindings: FeatureReferenceBinding[];
    } {
        const baseCommandParams = input.prototype.getDefinition().getCommandParams();
        const commandParams: ShellParams = {
            ...structuredClone(baseCommandParams),
            featureKind: 'shell',
            definition: structuredClone(input.definition),
            editingFeatureId: input.featureId ?? undefined,
            referenceBindings: buildShellModelTopologyBindings({
                upstreamBodyObjectId: baseCommandParams.upstreamBodyObjectId,
                definition: input.definition,
            }),
        };
        const definition = ShellFeatureDefinitionModel.fromParams({
            commandParams,
        });
        const bindings = (commandParams.referenceBindings ?? []).map((binding) => createFeatureReferenceBinding(binding));
        return {
            featureKind: 'shell',
            featureId: input.featureId ?? null,
            definition,
            bindings,
        };
    }
}
